export type DataResult<T> = {
    success: true;
    data: T;
};

export type ErrorResult<U> = {
    success: false;
    error: U;
};

export type Result<T, U = Error> = DataResult<T> | ErrorResult<U>;
export type DataResultFromResult<R> = R extends DataResult<infer T> ? T : never;
export type ErrorResultFromResult<R> =
    R extends DataResult<infer T> ? T : never;

export function dataResult<T>(data: T): DataResult<T> {
    return {
        success: true,
        data: data,
    };
}

/** @deprecated Use dataResult instead */
export const okResult = dataResult;

export function errorResult<U = Error>(
    // Force user to either override the generic type U, or accept Error.
    // Help prevent cases where user intends to have a Result<Error>
    // but accidentally creates Result<string> or something.
    error: NoInfer<U>,
): ErrorResult<U> {
    return {
        success: false,
        error: error,
    };
}

/** @deprecated Use errorResult instead */
export const errResult = errorResult;

// export function errorResultFromString(
//     message: string,
// ): ErrorResult<Error> {
//     return errorResult(new Error(message));
// }

export function errorResultFromException(
    exception: unknown,
): ErrorResult<Error> {
    if (exception instanceof Error) {
        return errorResult(exception);
    }

    return errorResult(
        new Error(`Caught exception of unknown type: ${String(exception)}`),
    );
}

/** @deprecated */
export function unwrap<T>(result: Result<T, Error>, error: string): T {
    if (result.success === false) {
        throw new Error(error, {
            cause: result.error,
        });
    }

    return result.data;
}

export function patchResultForCompatibilityIfRequired<T, U>(
    result:
        | {
              success: true;
              ok: T;
          }
        | {
              success: true;
              data: T;
          }
        | {
              success: false;
              err: U;
          }
        | {
              success: false;
              error: U;
          },
): Result<T, U> {
    if (result.success === true) {
        if ("data" in result) {
            return result;
        }

        return dataResult(result.ok);
    }

    if ("error" in result) {
        return result;
    }

    return errorResult(result.err);
}
